import type { ShiftComparisonException, ShiftComparisonReportRow } from 'shared-models';
import type { ShiftTipReportRow,ShiftTipReportEmployee,ShiftTipReportDaily } from 'shared-models';

export function appendReportRows<Row extends {id:string | number}>(current:Row[],incoming:Row[]):Row[] {
  const ids=new Set(current.map(row => row.id));
  return [...current,...incoming.filter(row => !ids.has(row.id))];
}

export function tipReportValues(row:ShiftTipReportRow | ShiftTipReportEmployee | ShiftTipReportDaily) {
  const clockLabel=(value:string | null | undefined) => value ? new Intl.DateTimeFormat('he-IL',{
    hour:'2-digit',minute:'2-digit',timeZone:'Asia/Jerusalem',
  }).format(new Date(value)) : '—';
  const allocation='allocationMode' in row ? row.allocationSource==='legacy' ? 'רישום קודם' : row.allocationMode==='own' ? 'מלצר בקופה' : 'משותפת' : '';
  const amounts={'שעות עבודה':minutesLabel(row.workedMinutes),'טיפ בסיס':row.baseTip,'השלמה':row.completion,'טיפ סופי':row.finalTip};
  if ('poolTip' in row) return {יום:new Intl.DateTimeFormat('he-IL',{weekday:'long',timeZone:'Asia/Jerusalem'}).format(new Date(`${row.date}T12:00:00Z`)),
    תאריך:row.date,חלוקה:allocation,'טיפים במזומן':row.tipCash,'טיפים באשראי':row.tipCredit,'טיפים אחרים':row.tipOther,
    'סך טיפים':row.poolTip,'שעות מתלמדים':row.traineeMinutes==null ? '—' : minutesLabel(row.traineeMinutes),
    'ניכוי מסעדה':row.restaurantDeduction ?? '—','ניכוי מתלמדים':row.traineeDeduction ?? '—','יתרה לחלוקה':row.remainingTips ?? '—',
    ...amounts,מצב:row.isLocked ? 'נעול' : 'לא נעול',
    'מקור שעות':row.hasLiveHours ? 'כולל שעות קופה עדכניות' : 'חישובים שמורים'};
  return 'date' in row ? {עובד:row.employeeName,תאריך:row.date,מחלקה:row.departmentName,משמרת:row.shiftName,
    תפקיד:row.roleName ?? '—',דרגה:row.tipGrade ?? '—',כניסה:clockLabel(row.clockIn),יציאה:clockLabel(row.clockOut),'טיפ לשעה':row.hourlyTip ?? '—',
    חלוקה:allocation,
    'סוג יום':row.typeShift==='daily' ? 'יומי' : row.typeShift==='morning' ? 'בוקר היסטורי' : 'ערב היסטורי',
    'מקור שעות':row.hoursSource==='livePos' ? 'קופה סגורה כעת' : 'חישוב שמור',...amounts,מצב:row.isLocked ? 'נעול' : 'לא נעול'}
    : {עובד:row.employeeName,תאריכים:row.dates,...amounts,'רישומים נעולים':row.lockedRecords,'רישומים לא נעולים':row.unlockedRecords,
      'מקור שעות':row.hasLiveHours ? 'כולל שעות קופה עדכניות' : 'חישובים שמורים'};
}

export const dateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const monthStart = () => {
  const date = new Date();
  return dateKey(new Date(date.getFullYear(), date.getMonth(), 1));
};

export const minutesLabel = (minutes: number | null) =>
  minutes === null ? 'פתוחה' : `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;

export const timeLabel = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat('he-IL', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
    : 'פתוחה';

const comparisonExceptions:Record<ShiftComparisonException,string> = {
  missingAttendance:'ללא נוכחות',unscheduledAttendance:'נוכחות ללא שיבוץ',missingClockOut:'חסר דיווח יציאה',
};
export function comparisonReportValues(row:ShiftComparisonReportRow) {
  const attendanceTime = (value:string | null) => value ? new Intl.DateTimeFormat('he-IL',{
    dateStyle:'short',timeStyle:'short',timeZone:'Asia/Jerusalem',
  }).format(new Date(value)) : 'חסר דיווח יציאה';
  return {עובד:row.employeeName,תאריך:row.date,מחלקה:row.planned?.sectionName ?? '',משמרת:row.planned?.shiftName ?? '',תפקיד:row.planned?.roleName ?? '',
    'התחלה מתוכננת':row.planned?.startHour ?? '', 'סיום מתוכנן':row.planned?.endHour ?? '',
    'כניסה בפועל':row.attendance ? attendanceTime(row.attendance.startShiftAt) : '',
    'יציאה בפועל':row.attendance ? attendanceTime(row.attendance.endShiftAt) : '',
    'שעות מתוכננות':minutesLabel(row.scheduledMinutes),'שעות בפועל':minutesLabel(row.workedMinutes===null ? null : Math.round(row.workedMinutes)),
    חריגות:row.exceptions.map(exception => comparisonExceptions[exception]).join(', ')};
}
